import { useRef } from 'react';

import FocusTarget from 'components/FocusTarget';
import { LoadingProductCardSkeletonItem } from 'components/LoadingSkeleton';
import { MicroContentChipLinks } from 'components/MicroContent';
import ProductCard from 'components/ProductCard';
import type { ValidSitecoreLink } from 'components/SitecoreLink';
import Skiplink from 'components/Skiplink';
import { useId } from 'hooks';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
} from 'hooks/gtm';
import type { ProductCard as ProductCardModel } from 'models/productCard';
import { cn } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import Carousel, {
	type ItemProps,
	type Props as CarouselProps,
} from './Carousel';

interface Props extends Omit<CarouselProps, 'items' | 'orientation'> {
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	isLoading?: boolean;
	microContentLinks?: ValidSitecoreLink[];
	microContentLinksTitle?: string;
	products: ProductCardModel[] | undefined;
	showAddToCartButton?: boolean;
	sizing?: 'full-width' | 'themed-block';
}

/** Carousel with product cards and trailing micro content links. */
export default function ProductCarousel({
	carouselClassName,
	className,
	gtmItemListId,
	gtmItemListName,
	isLoading,
	microContentLinks,
	microContentLinksTitle,
	products,
	showAddToCartButton,
	sizing = 'full-width',
	...carouselProps
}: Props) {
	const { tPlural } = useI18n();
	const skipTargetId = useId('skip-products');

	const { sendViewItemListEvent } = useProductListGTMEvents(
		gtmItemListId,
		gtmItemListName,
	);
	const sentIndicesRef = useRef(new Set<number>());
	const sendGtmViewEvent = (itemData: ItemProps[]) => {
		if (!products?.length) {
			return;
		}
		// Count all passed items as viewed, even if the user quickly scrolled
		// past them and didn't see them properly.
		const lastVisibleIndex = itemData.findLastIndex((item) => item.isVisible);
		const unsentPassedItems = itemData
			.slice(0, lastVisibleIndex + 1)
			.filter((item) => !sentIndicesRef.current.has(item.i));
		const items = filterTruthy(
			unsentPassedItems.map((item) => ({
				...item,
				product: products[item.i],
			})),
			'product',
		);
		if (items.length > 0) {
			sendViewItemListEvent(
				items.map((item) => ({ id: item.product.id, index: item.i + 1 })),
				// TODO: pageSize is pointless here since each item has an index.
				itemData.filter((item) => item.isVisible).length,
			);
			items.forEach((item) => {
				sentIndicesRef.current.add(item.i);
			});
		}
	};

	if (!isLoading && !products?.length) {
		return null;
	}

	const items = isLoading
		? range(6).map((i) => (
				<LoadingProductCardSkeletonItem
					key={i}
					showAddToCartButton={showAddToCartButton}
				/>
			))
		: (products?.map((product, i) => (
				<ProductCard
					key={product.id}
					product={product}
					productListIndex={i}
					gtmItemListId={gtmItemListId}
					gtmItemListName={gtmItemListName}
					// Padding to compensate for the expanded hit area of the stock store
					// selector button, which stretches outside the card and can cause
					// a vertical scrollbar.
					className="h-full pb-2"
					isColumnEvenHeight
					showAddToCartButton={
						showAddToCartButton &&
						product.isSellable &&
						!product.hasMultipleVariants
					}
				/>
			)) ?? []);

	if (!isLoading && is.arrayWithLength(microContentLinks)) {
		items.push(
			<MicroContentChipLinks
				key="micro-content-links"
				links={microContentLinks}
				title={microContentLinksTitle}
			/>,
		);
	}

	return (
		<div className={cn('relative', className)}>
			<Skiplink
				target={skipTargetId}
				text={tPlural('products_skiplink_text', items.length)}
				variant="absolute"
			/>
			<Carousel
				{...carouselProps}
				items={items}
				onUpdate={sendGtmViewEvent}
				carouselClassName={cn(
					'gap-x-r-md',
					sizing === 'full-width' && [
						'auto-cols-[60%]',
						'sm:auto-cols-[calc(50%-(var(--r-space-md)*0.5))]',
						'md:auto-cols-[calc(25%-(var(--r-space-md)*0.75))]',
						'lg:auto-cols-[calc(16.666%-(var(--r-space-md)*0.833))]',
					],
					sizing === 'themed-block' && [
						'auto-cols-[66%]',
						'sm:auto-cols-[calc(29%-(var(--r-space-md)*0.71))]',
						'md:auto-cols-[calc(23%-(var(--r-space-md)*0.77))]',
					],
					carouselClassName,
				)}
			/>
			<FocusTarget id={skipTargetId} />
		</div>
	);
}
ProductCarousel.displayName = 'ProductCarousel';
